/* eslint-disable camelcase */
import React, { useState } from 'react';
import clsx from 'clsx';
import { head, prop, find, propEq, filter, isEmpty, forEachObjIndexed } from 'ramda';
import { useTranslation } from 'react-i18next';
import { isBlank } from 'utils/helperMethods';
import PropTypes from 'prop-types';
import { Typography, TextField } from '@material-ui/core';
import PlacesAutocomplete, { geocodeByAddress } from 'lib/react-places-autocomplete';

import useStyles from './useStyles';

const TYPE_MAPPER = [
  {
    field: 'streetAddress1',
    componentType: 'street_number',
    componentField: 'short_name',
  },
  {
    field: 'streetAddress2',
    componentType: 'route',
    componentField: 'long_name',
  },
  {
    field: 'city',
    componentType: 'locality',
    componentField: 'long_name',
  },
  {
    field: 'state',
    componentType: 'administrative_area_level_1',
    componentField: 'short_name',
  },
  {
    field: 'zipCode',
    componentType: 'postal_code',
    componentField: 'short_name',
  },
  {
    field: 'lat',
    componentType: 'lat',
  },
  {
    field: 'lng',
    componentType: 'lng',
  },
];

const buildLocationAttrs = fields => {
  const mapper = TYPE_MAPPER.map(({ field }) => field);
  const attrs = mapper.reduce((acc, item) => {
    const field = fields.find(el => el.field === item);
    return { ...acc, [item]: field ? field.value : '' };
  }, {});
  const { streetAddress1, streetAddress2 } = attrs;
  const street = filter(value => !isBlank(value), [streetAddress1, streetAddress2]).join(' ');
  return { ...attrs, streetAddress: street };
};

const buildLocationString = attr => {
  const { streetAddress1, streetAddress2, city, state } = attr;
  return filter(value => !isBlank(value), [streetAddress1, streetAddress2, city, state]).join(', ');
};

const AddressSelect = props => {
  const {
    label,
    form: { values, setFieldValue },
  } = props;
  const { t } = useTranslation();
  const classes = useStyles();

  const initialValue = buildLocationString(values);
  const [inputAddress, setInputAddress] = useState(initialValue);
  const [error, setError] = useState('');

  const handleChangeAddressFields = attrs => {
    const setValue = (value, key) => setFieldValue(key, value);
    forEachObjIndexed(setValue, attrs);
  };

  const handleChange = address => {
    setInputAddress(address);

    if (isEmpty(address)) {
      const fields = TYPE_MAPPER.reduce((acc, { field }) => [...acc, { field, value: '' }], []);
      const attrs = buildLocationAttrs(fields);
      handleChangeAddressFields(attrs);
    }
  };

  const handleSelect = async address => {
    const fieldValues = [];
    try {
      const response = await geocodeByAddress(address);
      const components = prop('address_components', head(response));
      components.forEach(component => {
        const type = head(component.types);
        const mapper = find(propEq('componentType', type))(TYPE_MAPPER);
        if (mapper) {
          const { field, componentField } = mapper;
          fieldValues.push({ field, value: component[componentField] });
        }
      });

      const { location } = prop('geometry', head(response));
      fieldValues.push({ field: 'lat', value: location.lat() });
      fieldValues.push({ field: 'lng', value: location.lng() });

      const attrs = buildLocationAttrs(fieldValues);
      const locationString = buildLocationString(attrs);
      setInputAddress(locationString);
      handleChangeAddressFields(attrs);
    } catch (err) {
      setError(err);
    }
  };

  return (
    <PlacesAutocomplete
      value={inputAddress}
      onChange={handleChange}
      onSelect={handleSelect}
      searchOptions={{
        componentRestrictions: { country: 'us' },
        types: ['geocode'],
      }}
      onError={() => {}}
      clearItemsOnError
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className={classes.container}>
          <TextField
            className={classes.input}
            label={label}
            variant="outlined"
            {...getInputProps({})}
            InputProps={{
              style: {
                height: '40px',
              },
            }}
            InputLabelProps={{
              shrink: false,
            }}
          />
          <div className={classes.dropdown}>
            {!isEmpty(error) && (
              <Typography className={clsx(classes.error, classes.item)} variant="body1">
                {t('sharedComponents.AddressSelect.error')}
              </Typography>
            )}
            {loading && (
              <Typography variant="body1" className={classes.item}>
                {t('sharedComponents.AddressSelect.loading')}
              </Typography>
            )}
            {suggestions.map(suggestion => {
              return (
                <div
                  {...getSuggestionItemProps(suggestion, {})}
                  key={suggestion.placeId}
                  className={clsx(classes.item, { [classes.active]: suggestion.active })}
                >
                  <Typography variant="body1">{suggestion.description.replace(', USA', '')}</Typography>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  );
};

AddressSelect.propTypes = {
  label: PropTypes.string.isRequired,
  values: PropTypes.shape(),
};

AddressSelect.defaultProps = {
  values: {},
};

export default AddressSelect;



// WEBPACK FOOTER //
// ./src/sharedComponents/AddressSelect/AddressSelect.js